/**
 * «Как это работает» — попап с шагами.
 *
 * Приём из polymarket.com: вместо страницы с текстом — короткая проводка по
 * шагам, где на каждом шаге одна мысль и одна картинка. Человек, который
 * первый раз видит промо-платформу, не станет читать раздел «о честности»:
 * он хочет понять, что делать с кодом на крышке.
 *
 * Картинки нарисованы тут же, разметкой и токенами: иллюстрации в виде
 * растра пришлось бы рисовать отдельно под тёмную и светлую тему, а сами
 * шаги меняются чаще, чем кажется.
 */
import { Button, Icon } from '@allswipes/ui';
import { useEffect, useState } from 'react';

import { useT } from '@allswipes/i18n';

interface Step {
  title: string;
  body: string;
  /** Рисунок шага. Перевод приходит аргументом: список лежит вне компонента,
      а хук можно звать только внутри. */
  art: (t: (text: string) => string) => JSX.Element;
}

const STEPS: Step[] = [
  {
    title: 'Найди код',
    body: 'Он под крышкой, на этикетке или в чеке. Один код — одно участие, повторно его не примут ни у кого.',
    art: (t) => (
      <div className="hiw-art">
        <div className="hiw-code">CC-0 4 2 1 7</div>
        <div className="hiw-cap">{t('код под крышкой')}</div>
      </div>
    ),
  },
  {
    title: 'Отсканируй и выбери цифру',
    body: 'У акций с розыгрышем ты выбираешь одну цифру из трёх. Цифры всех участников складываются, из суммы получается множитель — и он входит в сид розыгрыша.',
    art: (t) => (
      <div className="hiw-art">
        <div className="hiw-picks">
          <span>3</span>
          <span className="on">5</span>
          <span>8</span>
        </div>
        <div className="hiw-cap">{t('твой выбор влияет на результат')}</div>
      </div>
    ),
  },
  {
    title: 'Жди розыгрыш или получи приз сразу',
    body: 'В розыгрыше номер билета сразу попадает в открытый список. В мгновенных механиках приз приходит в ту же секунду — его посчитал сервер по заранее зафиксированному сиду.',
    art: (t) => (
      <div className="hiw-art">
        <div className="hiw-cd">2{t('д')} 14:07:33</div>
        <div className="hiw-cap">{t('до розыгрыша · 12 призов')}</div>
      </div>
    ),
  },
  {
    title: 'Пересчитай результат сам',
    body: 'После розыгрыша раскрывается сид сервера, а публичный сид — это тираж гослото. Верификатор в твоём браузере считает победителей заново и сравнивает со списком.',
    art: (t) => (
      <div className="hiw-art">
        <div className="hiw-hash">
          <span>f34cf194…</span>
          <Icon name="check" size={16} />
        </div>
        <div className="hiw-cap">{t('совпадает с опубликованным')}</div>
      </div>
    ),
  },
];

export function HowItWorks({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [step, setStep] = useState(0);
  const current = STEPS[step]!;
  const last = step === STEPS.length - 1;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && !last) setStep((s) => s + 1);
      if (e.key === 'ArrowLeft' && step > 0) setStep((s) => s - 1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, last, step]);

  return (
    <div className="ovl" role="dialog" aria-modal="true" aria-label={t('Как это работает')} onClick={onClose}>
      <div className="sheet hiw" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ marginBottom: 14 }}>
          <span className="lab">{t('Как это работает')}</span>
          <span className="spacer" />
          <Button variant="ghost" size="sm" icon="close" ariaLabel={t('Закрыть')} onClick={onClose} />
        </div>

        {current.art(t)}

        <div className="hiw-step">
          {step + 1}. {t(current.title)}
        </div>
        <p className="sub" style={{ marginTop: 8 }}>
          {t(current.body)}
        </p>

        <div className="hiw-dots" aria-hidden="true">
          {STEPS.map((_, i) => (
            <span key={i} className={i === step ? 'on' : ''} />
          ))}
        </div>

        <div className="row" style={{ gap: 8, marginTop: 14 }}>
          {step > 0 && (
            <Button variant="out" onClick={() => setStep(step - 1)}>
              {t('Назад')}
            </Button>
          )}
          <Button variant="pri" block onClick={() => (last ? onClose() : setStep(step + 1))}>
            {last ? t('Понятно') : t('Далее')}
          </Button>
        </div>
      </div>
    </div>
  );
}
